/**
 * Offline sync status for banners and chips (Phase 16C C8). Counts and ids only.
 */
import { useCallback, useEffect, useState } from 'react';
import { Offline } from '../services/offline/offlineServices';
import { OUTBOX_CHANGED_EVENT, OutboxOp, isUnsynced } from '../services/offline/outbox';
import { OfflineSession } from '../services/offline/offlineSession';
import type { SyncEngineState } from '../services/offline/syncEngine';

/** window event asking the shell to show Sync Center */
export const OPEN_SYNC_CENTER_EVENT = 'open-sync-center';

export const openSyncCenter = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(OPEN_SYNC_CENTER_EVENT));
};

export interface SyncStatus {
  /** The device has an unlocked offline store */
  active: boolean;
  pending: number;
  conflicts: number;
  attention: number;
  /** Unsynced ops for the given visit */
  forVisit: number;
  ops: OutboxOp[];
  engine: SyncEngineState;
  retry: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useSyncStatus(visitId?: string | null): SyncStatus {
  const [ops, setOps] = useState<OutboxOp[]>([]);
  const [active, setActive] = useState<boolean>(Boolean(OfflineSession.getStore()));
  const [engine, setEngine] = useState<SyncEngineState>(Offline.engine.getState());

  const refresh = useCallback(async () => {
    const unlocked = Boolean(OfflineSession.getStore());
    setActive(unlocked);
    if (!unlocked) {
      setOps([]);
      return;
    }
    try {
      setOps((await Offline.outbox.list()).filter(isUnsynced));
    } catch {
      setOps([]);
    }
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener(OUTBOX_CHANGED_EVENT, refresh);
    const offSession = OfflineSession.subscribe(() => { refresh(); });
    const offEngine = Offline.engine.subscribe(() => setEngine(Offline.engine.getState()));
    return () => {
      window.removeEventListener(OUTBOX_CHANGED_EVENT, refresh);
      offSession();
      offEngine();
    };
  }, [refresh]);

  const retry = useCallback(async () => {
    await Offline.engine.retryNow().catch(() => undefined);
    await refresh();
  }, [refresh]);

  return {
    active,
    pending: ops.length,
    conflicts: ops.filter((o) => o.status === 'conflict').length,
    attention: ops.filter((o) => o.status === 'needs_attention').length,
    forVisit: visitId ? ops.filter((o) => o.visitId === visitId).length : ops.length,
    ops,
    engine,
    retry,
    refresh,
  };
}
